import { useMemo } from 'react';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import type { RevampCategoryChip } from '../type';

interface EditorialFilterBarProps {
    /** Every category, in nav order — the same list on every page. */
    items: RevampCategoryChip[];
    /** The category being browsed, for the breadcrumb trail. */
    active?: RevampCategoryChip;
    searchFilters: ReturnType<typeof useSearchFilters>;
    className?: string;
}

const ARTICLES_ROOT = '/articles';

/**
 * The category rail and refine control beneath the masthead.
 *
 * This is the shared `DirectoryPillBar` the products, projects and
 * professionals directories use, so editorial inherits the same refine panel,
 * applied-filter chips, drag-scroll rail and active-pill behaviour rather than
 * a lookalike. Two things differ:
 *
 * - the keyword field is off, because this listing is served by the CMS article
 *   category query and has no keyword search behind it;
 * - no sort options are offered — the query orders by publish date and nothing
 *   on the page can change that.
 */
export const EditorialFilterBar = ({
    items,
    active,
    searchFilters,
    className,
}: EditorialFilterBarProps) => {
    const breadcrumbs = useMemo(
        () => [
            { href: ARTICLES_ROOT, label: 'Articles' },
            ...(active ? [{ href: active.link, label: active.title }] : []),
        ],
        [active],
    );

    return (
        <div className={className}>
            <DirectoryPillBar
                breadcrumbs={breadcrumbs}
                searchFilters={searchFilters}
                sortOptions={[]}
                categoryPills={items}
                rootPath={ARTICLES_ROOT}
                rootLabel="All Articles"
                resultName="Article"
                showSearch={false}
            />
        </div>
    );
};
